import { Virtuoso } from 'react-virtuoso';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import ObjectItem from './ObjectItem/ObjectItem';
import './ObjectBrowser.css';
import TopBar from './TopBar/TopBar';
import { Api } from '../../App';

export interface Filter {
    name: string,
    type: 'radio'|'range',
    filters: string;
}
export interface RadioFilter extends Filter {
    type: 'radio';
    options: Array<any>|Promise<Array<any>>;
}
export interface RangeFilter extends Filter {
    type: 'range';
    min: number|Promise<number>;
    max: number|Promise<number>;
    step?: number;
}

export interface ObjectSchema {
    name: string,
    description: string|null,
    image: string|null,
    value: string,
    attributes: any;
}

export enum ObjectType { null, 'MATERIAL', 'ENDMILL', 'DRILL' }

function filterItems(items: Array<ObjectSchema>, filters: Array<RadioFilter|RangeFilter>, filterStates: Array<string|Array<number>|null>) {
    items = items.slice();
    filters.forEach((filter, index) => {
        if (filterStates[index] === null) return;
        switch(filter.type) {
            case 'radio':
                items = items.filter(item => item.attributes[filter.filters] === filterStates[index]);
                break;
            case 'range':
                items = items.filter(item =>
                    item.attributes[filter.filters] >= (filterStates as any)[index][0] &&
                    item.attributes[filter.filters] <= (filterStates as any)[index][1]
                );
                break;
        }
    })
    return items;
}


function ObjectBrowser(
    {style={}, type, changeStep, searchPlaceholder, objects, filters, children, footer} :
    {style?: any, type: ObjectType, changeStep: any, searchPlaceholder: string, objects: Array<ObjectSchema>|Promise<Array<ObjectSchema>>, filters: Array<RadioFilter|RangeFilter|string>, children?: any, footer?: any}
)
{
    const virtuoso = useRef(null);
    const [resolvedObjects, setObjects] = useState(objects);
    const [query, setQuery] = useState('');
    const [openIndex, setOpenIndex] = useState(-1);
    const [filterStates, setFilterStates] = useState<Array<string|Array<number>|null>>(filters.map(() => null));


    useEffect(() => {
        if (objects instanceof Promise) {
            objects.then((objs) => setObjects(objs));
        }
    });

    let filteredParams = {}
    filterStates.forEach((state, index) => {
        if (state !== null && typeof filters[index] !== 'string') {
            // @ts-ignore - TypeScript ignores the above typeof check, so this must be done
            filteredParams[filters[index].filters] = state;
        }
    })
    
    let objectData = useMemo(() => {
        // Once data is resolved (loaded from server), filter it and render it
        let data: any[] = [];
        if (resolvedObjects instanceof Array) {
            data = filterItems(resolvedObjects, filters.filter(f => typeof f !== "string") as any, filterStates)
                .filter((object) => {
                    return (
                        !query ||
                        object.name.toLowerCase().includes(query) ||
                        object.description?.toLowerCase().includes(query))
                })

            // Custom Materials from Material Groups.
            if (type === ObjectType.MATERIAL && filterStates[0] !== null && Api.cached.get('material_groups') instanceof Array) {
                let materialGroup = Api.cached.get('material_groups').filter((x:any) => x.id === filterStates[0])[0];
                data.unshift({
                    name: '',
                    description: `Custom ${materialGroup.material_description} Material`,
                    image: null,
                    value: `material_group-${materialGroup.id}`,
                    attributes: {
                        // I have to fetch this from the API because the material groups given to ObjectBrowser
                        // is a promise, and I can't resolve that while rendering.
                        ...materialGroup,
                        machining_group: materialGroup.id,
                        isSuggestion: true
                    }
                })
            }
        }
        return data;
    }, [type, resolvedObjects, filterStates, filters, query]);
    let hasTopItem = objectData.length > 0 && objectData[0].attributes.isSuggestion;
    

    const computeItemKey = useCallback((index:number) => `${type}-${objectData[index].value}`, [objectData, type])

    let virtuosoProps = resolvedObjects instanceof Array ? {
        ref: virtuoso,
        computeItemKey,
        totalCount: objectData.length,
        itemContent: (index:number) => (
            <ObjectItem
                open={openIndex === objectData[index].value}
                setOpen={() => setOpenIndex(openIndex === objectData[index].value ? -1 : objectData[index].value)}
                changeStep={changeStep}
                type={type}
                object={objectData[index]}
            />
        ),
        components: {
            Footer: () => (footer)
        },
        style: {
            height: '100%',
            overflow: 'auto',
            borderTop: '1px solid #ccc'
        }
    } : {}
    
    return (
        <div className="objectBrowser" style={style}>
            <TopBar 
                searchPlaceholder={`Search ${objectData.length} ${searchPlaceholder}`}
                searchValue={query}
                setSearchValue={setQuery}
                filters={filters}
                filterValues={filterStates}
                setFilterValues={setFilterStates}
            />
            {resolvedObjects instanceof Array ?
                objectData.length > 0 ?
                    hasTopItem ?
                    // Due to the way virtuoso renders pinned items, we must use a different object for non-pins.
                    <Virtuoso {...virtuosoProps} topItemCount={1} /> :
                    <Virtuoso {...virtuosoProps} key={0} /> :
                <div style={{borderTop: '1px solid #ccc', paddingTop: '40px', textAlign: 'center', paddingBottom: '20vh'}}>No results found for the specified query and filters.<br />If you're trying to find something with a filter, try <span className="link" onClick={() => setQuery('')}>clearing your search query</span>.<br />{type !== ObjectType.MATERIAL && "If you're trying to find a tool, try selecting a more compatible material."}</div> :
                <div>Loading...</div>
            }

            {children}
        </div>
    )
}

export default ObjectBrowser;